import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
} from "firebase/auth";
import { auth, googleProvider } from "../services/firebase";
import { session } from "../state/session";
import { router } from "../router/router";

@customElement("app-login")
export class AppLogin extends LitElement {
  @state() private email = "";
  @state() private password = "";
  @state() private loading = false;
  @state() private errorMsg = "";
  @state() private infoMsg = "";
  @state() private showPasswordMode = false;

  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      background:
        radial-gradient(900px 600px at 0% -10%, rgba(32, 110, 58, 0.1), transparent 55%),
        radial-gradient(900px 600px at 100% 0%, rgba(32, 110, 58, 0.08), transparent 50%),
        linear-gradient(180deg, #f5f8f5, #ebf2ec);
      color: #183324;
      font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }

    .page {
      max-width: 1100px;
      margin: 0 auto;
      padding: 1rem 1rem 3rem;
    }

    .header-gap {
      padding-top: 105px;
    }

    .shell {
      display: grid;
      grid-template-columns: minmax(0, 1.05fr) minmax(360px, 0.95fr);
      gap: 20px;
      align-items: start;
    }

    .hero {
      padding: 24px;
      border-radius: 24px;
      border: 1px solid rgba(32, 110, 58, 0.12);
      background: linear-gradient(135deg, rgba(32, 110, 58, 0.08), rgba(255, 255, 255, 0.96));
      box-shadow: 0 12px 34px rgba(24, 51, 36, 0.08);
    }

    .heroTitle {
      margin: 0;
      font-size: 2.2rem;
      line-height: 1;
      font-weight: 800;
      letter-spacing: -0.04em;
    }

    .heroSub {
      margin-top: 0.8rem;
      color: rgba(24, 51, 36, 0.72);
      line-height: 1.55;
      font-size: 1rem;
      max-width: 620px;
    }

    .heroList {
      margin: 1.1rem 0 0;
      padding: 0;
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .heroList li {
      display: flex;
      gap: 10px;
      align-items: flex-start;
      color: rgba(24, 51, 36, 0.8);
      line-height: 1.45;
      font-size: 0.95rem;
    }

    .dot {
      width: 10px;
      height: 10px;
      margin-top: 0.3rem;
      border-radius: 999px;
      background: #206e3a;
      flex: 0 0 auto;
    }

    .stack {
      display: flex;
      flex-direction: column;
      gap: 18px;
    }

    .panel {
      border-radius: 22px;
      border: 1px solid rgba(32, 110, 58, 0.12);
      background: linear-gradient(180deg, #ffffff, #f8fbf8);
      box-shadow: 0 10px 30px rgba(24, 51, 36, 0.06);
      overflow: hidden;
    }

    .panelHeader {
      padding: 18px 20px 12px;
      border-bottom: 1px solid rgba(32, 110, 58, 0.08);
    }

    .panelTitle {
      margin: 0;
      font-size: 1.15rem;
      font-weight: 800;
      letter-spacing: -0.02em;
    }

    .panelSub {
      margin-top: 0.45rem;
      color: rgba(24, 51, 36, 0.68);
      line-height: 1.45;
      font-size: 0.92rem;
    }

    .panelBody {
      padding: 16px 20px 20px;
    }

    .inviteBanner {
      margin-bottom: 14px;
      padding: 13px 14px;
      border-radius: 16px;
      border: 1px solid rgba(32, 110, 58, 0.18);
      background: rgba(32, 110, 58, 0.08);
      color: #206e3a;
      font-weight: 700;
      line-height: 1.4;
      font-size: 0.92rem;
    }

    .providerGrid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 10px;
    }

    .btn {
      width: 100%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 0.95rem 1rem;
      border-radius: 16px;
      border: 1px solid rgba(32, 110, 58, 0.14);
      background: #ffffff;
      color: #183324;
      font-weight: 800;
      font-size: 0.95rem;
      cursor: pointer;
      box-shadow: 0 6px 18px rgba(24, 51, 36, 0.04);
      transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
    }

    .btn:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 12px 24px rgba(24, 51, 36, 0.08);
      border-color: rgba(32, 110, 58, 0.24);
    }

    .btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }

    .btn.primary {
      background: linear-gradient(135deg, #206e3a, #2f8a4b);
      color: white;
      border-color: transparent;
    }

    .btn.secondary {
      background: rgba(32, 110, 58, 0.06);
      color: #206e3a;
      border-color: rgba(32, 110, 58, 0.14);
      box-shadow: none;
    }

    .btn.subtle {
      background: transparent;
      box-shadow: none;
    }

    .orRow {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 16px 0;
      color: rgba(24, 51, 36, 0.45);
      font-size: 0.82rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }

    .orRow::before,
    .orRow::after {
      content: "";
      flex: 1;
      height: 1px;
      background: rgba(32, 110, 58, 0.12);
    }

    .field {
      display: flex;
      flex-direction: column;
      gap: 6px;
      margin-top: 12px;
    }

    label {
      font-size: 0.84rem;
      font-weight: 700;
      color: rgba(24, 51, 36, 0.72);
    }

    input {
      width: 100%;
      box-sizing: border-box;
      padding: 0.95rem 1rem;
      border-radius: 16px;
      border: 1px solid rgba(32, 110, 58, 0.14);
      background: rgba(255, 255, 255, 0.94);
      color: #183324;
      font: inherit;
      outline: none;
    }

    input:focus {
      border-color: rgba(32, 110, 58, 0.32);
      box-shadow: 0 0 0 3px rgba(32, 110, 58, 0.08);
    }

    input::placeholder {
      color: rgba(24, 51, 36, 0.42);
    }

    .helper {
      margin-top: 8px;
      font-size: 0.84rem;
      color: rgba(24, 51, 36, 0.62);
      line-height: 1.45;
    }

    .message {
      margin-top: 14px;
      padding: 12px 14px;
      border-radius: 14px;
      line-height: 1.45;
      font-size: 0.9rem;
      white-space: pre-wrap;
    }

    .message.error {
      background: #fef2f2;
      border: 1px solid rgba(180, 35, 24, 0.18);
      color: #991b1b;
    }

    .message.info {
      background: #f0fdf4;
      border: 1px solid rgba(22, 101, 52, 0.18);
      color: #166534;
    }

    .footerNote {
      margin-top: 16px;
      font-size: 0.86rem;
      color: rgba(24, 51, 36, 0.68);
      line-height: 1.55;
    }

    .miniCard {
      padding: 16px;
      border-radius: 18px;
      border: 1px solid rgba(32, 110, 58, 0.1);
      background: rgba(255, 255, 255, 0.82);
    }

    .miniTitle {
      margin: 0 0 6px;
      font-size: 0.95rem;
      font-weight: 800;
    }

    .miniText {
      margin: 0;
      font-size: 0.88rem;
      color: rgba(24, 51, 36, 0.7);
      line-height: 1.5;
    }

    @media (max-width: 900px) {
      .shell {
        grid-template-columns: 1fr;
      }

      .heroTitle {
        font-size: 1.9rem;
      }
    }
  `;

  private capturePendingInviteFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const clubId = (params.get("clubId") ?? "").trim();
    const inviteId = (params.get("inviteId") ?? params.get("invite") ?? "").trim();

    if (clubId && inviteId) {
      session.setPendingInvite({ clubId, inviteId });
    }
  }

  private pendingInviteLoginUrl(): string {
    const pending = session.getPendingInvite();
    if (!pending?.clubId || !pending?.inviteId) {
      return `${window.location.origin}/login`;
    }

    const params = new URLSearchParams({
      clubId: pending.clubId,
      inviteId: pending.inviteId,
    });

    return `${window.location.origin}/login?${params.toString()}`;
  }

  connectedCallback() {
    super.connectedCallback();

    session.bootstrap();
    this.capturePendingInviteFromUrl();

    onAuthStateChanged(auth, (u) => {
      if (!u) return;
      this.routeAfterAuth();
    });

    void this.handleRedirectResult();
    void this.handleEmailLinkIfPresent();
  }

  render() {
    const pending = session.getPendingInvite();

    return html`
      <app-header></app-header>
      <div class="page header-gap">
        <app-logo ?enableBack="true"></app-logo>

        <div class="shell">
          <section class="hero">
            <h1 class="heroTitle">Welcome back</h1>
            <div class="heroSub">
              Sign in to access your club, confirm availability, view finances, and stay connected with Appleton CC.
            </div>

            <ul class="heroList">
              <li><span class="dot"></span><span>Use Google for the quickest sign-in.</span></li>
              <li><span class="dot"></span><span>Prefer email? We can send you a magic sign-in link.</span></li>
              <li><span class="dot"></span><span>Email and password sign-in is available if you already use it.</span></li>
            </ul>

            <div class="stack" style="margin-top:18px;">
              <div class="miniCard">
                <h2 class="miniTitle">Invite-only clubs</h2>
                <p class="miniText">
                  You do not register as a player here. You simply sign in, then your invite links your account to the club.
                </p>
              </div>

              <div class="miniCard">
                <h2 class="miniTitle">Less confusion, more trust</h2>
                <p class="miniText">
                  This page keeps the most common sign-in route at the top and places the less-used options underneath.
                </p>
              </div>
            </div>
          </section>

          <section class="panel">
            <div class="panelHeader">
              <h2 class="panelTitle">Sign in</h2>
              <div class="panelSub">
                ${pending ? "Use any option below to accept your club invite." : "Choose the sign-in method that suits you best."}
              </div>
            </div>

            <div class="panelBody">
              ${pending
                ? html`
                    <div class="inviteBanner">
                      You have a pending club invite. Sign in first, then we’ll take you straight to the join step.
                    </div>
                  `
                : null}

             <div class="providerGrid">
  <button
    class="btn primary"
    ?disabled=${this.loading}
    @click=${() => this.signInWithGoogle()}
  >
    Continue with Google
  </button>
</div>

              <div class="orRow">or use email</div>

              <div class="field">
                <label>Email</label>
                <input
                  type="email"
                  autocomplete="email"
                  .value=${this.email}
                  @input=${(e: InputEvent) => (this.email = (e.target as HTMLInputElement).value)}
                  placeholder="you@example.com"
                />
              </div>

              <button
                class="btn secondary"
                ?disabled=${this.loading || !this.email.trim()}
                @click=${() => this.sendMagicLink()}
              >
                Email me a sign-in link
              </button>

              <div class="helper">
                This is the simplest email option. We send you a secure link so you can sign in without remembering a password.
              </div>

              <div class="orRow">other option</div>

              <button
                class="btn subtle"
                ?disabled=${this.loading}
                @click=${() => (this.showPasswordMode = !this.showPasswordMode)}
              >
                ${this.showPasswordMode ? "Hide password sign-in" : "Use email and password instead"}
              </button>

              ${this.showPasswordMode
                ? html`
                    <div class="field">
                      <label>Password</label>
                      <input
                        type="password"
                        autocomplete="current-password"
                        .value=${this.password}
                        @input=${(e: InputEvent) => (this.password = (e.target as HTMLInputElement).value)}
                        placeholder="Enter your password"
                      />
                    </div>

                    <div class="providerGrid" style="margin-top:12px;">
                      <button
                        class="btn"
                        ?disabled=${this.loading}
                        @click=${() => this.signInWithPassword()}
                      >
                        Sign in with password
                      </button>

                      <button
                        class="btn subtle"
                        ?disabled=${this.loading}
                        @click=${() => this.resetPassword()}
                      >
                        Forgot password?
                      </button>
                    </div>
                  `
                : nothing}

              ${this.errorMsg ? html`<div class="message error">${this.errorMsg}</div>` : nothing}
              ${this.infoMsg ? html`<div class="message info">${this.infoMsg}</div>` : nothing}

              <div class="footerNote">
                Once signed in, we will take you to the right place automatically.
              </div>
            </div>
          </section>
        </div>
      </div>
    `;
  }

  private routeAfterAuth() {
    const pending = session.getPendingInvite();
    if (pending) {
      router.navigate(
        `/join?clubId=${encodeURIComponent(pending.clubId)}&inviteId=${encodeURIComponent(pending.inviteId)}`
      );
      return;
    }

    router.navigate("/");
  }

  private setLoading(v: boolean) {
    this.loading = v;
  }

  private showError(msg: string) {
    this.errorMsg = msg;
    this.infoMsg = "";
    setTimeout(() => (this.errorMsg = ""), 10000);
  }

  private showInfo(msg: string) {
    this.infoMsg = msg;
    this.errorMsg = "";
    setTimeout(() => (this.infoMsg = ""), 10000);
  }

private async signInWithGoogle() {
  this.setLoading(true);
  this.errorMsg = "";
  this.infoMsg = "";

  try {
    try {
      await signInWithPopup(auth, googleProvider);
      this.routeAfterAuth();
    } catch {
      await signInWithRedirect(auth, googleProvider);
    }
  } catch (e: any) {
    this.showError(e?.message ?? "Google sign-in failed.");
  } finally {
    this.setLoading(false);
  }
}

  private async handleRedirectResult() {
    try {
      const result = await getRedirectResult(auth);
      if (result?.user) this.routeAfterAuth();
    } catch (e: any) {
      if (e?.message) this.showError(e.message);
    }
  }

  private async sendMagicLink() {
    const email = this.email.trim();
    if (!email) return;

    this.setLoading(true);
    try {
      const actionCodeSettings = {
        // Preserve pending invite details inside the email link. This prevents users
        // opening the magic link in a fresh tab/browser and losing the invite context.
        url: this.pendingInviteLoginUrl(),
        handleCodeInApp: true,
      };

      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      localStorage.setItem("emailForSignIn", email);
      this.showInfo("Check your email — we’ve sent you a sign-in link.");
    } catch (e: any) {
      this.showError(e?.message ?? "Failed to send sign-in link.");
    } finally {
      this.setLoading(false);
    }
  }

  private async handleEmailLinkIfPresent() {
    try {
      this.capturePendingInviteFromUrl();
      const href = window.location.href;
      if (!isSignInWithEmailLink(auth, href)) return;

      this.setLoading(true);

      const stored = localStorage.getItem("emailForSignIn") ?? "";
      const email = this.email.trim() || stored;

      if (!email) {
        this.showError("Enter your email to complete sign-in.");
        return;
      }

      await signInWithEmailLink(auth, email, href);
      localStorage.removeItem("emailForSignIn");
      this.routeAfterAuth();
    } catch (e: any) {
      this.showError(e?.message ?? "Email link sign-in failed.");
    } finally {
      this.setLoading(false);
    }
  }

  private async signInWithPassword() {
    const email = this.email.trim();
    const password = this.password;

    if (!email || !password) {
      this.showError("Email and password must not be empty.");
      return;
    }

    this.setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      this.routeAfterAuth();
    } catch (e: any) {
      this.showError(e?.message ?? "Password sign-in failed.");
    } finally {
      this.setLoading(false);
    }
  }

  private async resetPassword() {
    const email = this.email.trim();
    if (!email) {
      this.showError("Enter your email first.");
      return;
    }

    this.setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      this.showInfo("Password reset email sent.");
    } catch (e: any) {
      this.showError(e?.message ?? "Failed to send reset email.");
    } finally {
      this.setLoading(false);
    }
  }
}